// components/SettingsOverlay.jsx — Anamoria SPA
// v0.1 — Settings overlay-on-feed (PR1, flag settings_overlay). Presents the
//   settings surface as a WAI-ARIA modal dialog over the page it was triggered
//   from (the feed). role=dialog + aria-modal, focus trap, Escape / scrim-click
//   close (navigate(-1) back to the feed), body scroll-lock, focus returned to
//   the trigger on close. Desktop = panel over the dimmed feed; mobile =
//   full-screen (Apple HIG / NN/g / Material 3: a navigational multi-panel
//   surface is the full-screen case, not a sheet). Hosts <SettingsPage inOverlay/>.
//   Plan: settings-overlay-model-plan-DRAFT-for-review v0.2.

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SettingsOverlay.module.css';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function SettingsOverlay({ children }) {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const prevFocusRef = useRef(null);

  const close = useCallback(() => navigate(-1), [navigate]);

  useEffect(() => {
    prevFocusRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const focusables = () =>
      panel ? Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null) : [];
    // Initial focus into the dialog (first focusable, else the panel itself).
    const first = focusables()[0];
    (first || panel)?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
      if (e.key === 'Tab') {
        const f = focusables();
        if (f.length === 0) { e.preventDefault(); panel?.focus(); return; }
        const firstEl = f[0];
        const lastEl = f[f.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [close]);

  return (
    <div className={styles.backdrop} onClick={close}>
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
